import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { Classnames, IconList } from '@snowit/icetypes';
import type { A11yEvent } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import { Flex } from '@snowit/ui';
import testId from '@snowit/utils/testId';

import Icon from '../Icon';
import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Tabs.module.scss';

export interface ITab<T extends string> {
  key?: string;
  icon?: IconList;
  label?: string;
  hint?: string | number;
  value: T;
  active: boolean;
  disabled?: boolean;
  error?: boolean;
  hidden?: boolean;
  ariaControls?: string;
}

export interface ITabs<T extends string> {
  theme?: 'default' | 'switch' | 'sidebar';
  size?: 'df' | 'sm';
  tabs: ITab<T>[];
  onClick?: (tab: ITab<T>, event: MouseEvent<HTMLDivElement>) => void;
  classNames?: Classnames<'wrapper' | 'tab' | 'icon' | 'label' | 'hint'>;
  /** Jest/Cypress test string */
  test?: string;
}

const Tabs = <T extends string>(props: ITabs<T>) => {
  const { theme = 'default', size = 'df', tabs, onClick, classNames, test } = props;
  const a11yH = useA11y();
  const [activeIndex, setActiveIndex] = useState(-1);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const tabsRefs = useRef<(HTMLDivElement | null)[]>([]);

  const activeTab = useMemo(() => {
    return tabs.findIndex((tab) => tab.active && !tab.hidden);
  }, [tabs]);

  useLayoutEffect(() => {
    const tab = tabsRefs.current[activeTab];
    if (parentRef.current && tab) {
      const { offsetWidth, offsetLeft, offsetTop } = tab;
      const { style } = parentRef.current;
      style.setProperty('--lw-tab', `${offsetWidth}px`);
      style.setProperty('--lleft-tab', `${offsetLeft}px`);
      style.setProperty('--ltop-tab', `${offsetTop}px`);
    }
  }, [activeTab, tabsRefs.current, parentRef.current]);

  const handleClick = (tab: ITab<T>, event: MouseEvent<HTMLDivElement>) => {
    if (typeof onClick === 'function') {
      onClick(tab, event);
    }
  };

  const handleKeyDown = (tab: ITab<T>, event: A11yEvent<HTMLDivElement>) => {
    a11yH.handleListNav({
      event: event as KeyboardEvent<HTMLDivElement>,
      active: activeIndex,
      total: tabs.length,
      setIndex: (index: number) => {
        setActiveIndex(index);
        if (tabsRefs.current[index]) {
          tabsRefs.current[index]!.focus();
        }
      },
      onEnter: () => handleClick(tab, event as MouseEvent<HTMLDivElement>),
      onEsc: () => {},
      blockDefault: () => true,
    });
  };

  const renderIcon = (tab: ITab<T>) => {
    if (!tab.icon && !tab.error) return null;

    const ICON = tab.error ? 'error' : tab.icon;
    return <Icon className={cx(s.icon, classNames?.icon)} name={ICON} />;
  };

  const renderLabel = (tab: ITab<T>) => {
    if (!tab.label) return null;
    return (
      <Typo className={cx(s.label, classNames?.label)} text={tab.label} tag={'p'} size={'df'} />
    );
  };

  const renderHint = (tab: ITab<T>) => {
    if (!tab.hint) return null;
    return <Typo className={cx(s.hint, classNames?.hint)} text={tab.hint} tag={'p'} size={'sm'} />;
  };

  const renderTab = (tab: ITab<T>, i: number) => {
    if (tab.hidden) return null;
    return (
      <div
        ref={(el) => {
          tabsRefs.current[i] = el;
        }}
        key={tab.value || tab.key}
        className={cx(s.tab, classNames?.tab, {
          [s.active]: tab.active,
          [s.disabled]: tab.disabled,
          [s.error]: tab.error,
        })}
        onClick={(e) => handleClick(tab, e)}
        onKeyDown={(e) => handleKeyDown(tab, e)}
        onFocus={() => setActiveIndex(i)}
        role={'tab'}
        tabIndex={activeTab === i ? 0 : -1}
        aria-selected={tab.active}
        aria-controls={tab.ariaControls}
        {...testId(tab.value)}
      >
        <Flex justify={'center'} align={'center'} gap={[0.5]}>
          {renderIcon(tab)}
          {renderLabel(tab)}
          {renderHint(tab)}
        </Flex>
      </div>
    );
  };

  return (
    <div
      ref={parentRef}
      className={cx(s.wrapper, classNames?.wrapper, {
        [s[theme]]: true,
        [s[size]]: true,
      })}
      style={{ '--l-item-number': tabs?.length } as CSSProperties}
      role={'tablist'}
      {...testId(test)}
    >
      {tabs.map(renderTab).filter(Boolean)}
    </div>
  );
};

export default Tabs;
